import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import { listWellSamples, listWells } from '../../api/data';
import { DashboardOptions, KrTypes, useAppContext } from '../AppContext';
import { useSelected } from '../selectedContext';
import {
  RefinementBlockFamily,
  useCorrelation
} from '../Correlation';
import {
  NotificationAnchorId,
  NotificationCenterItem,
  NotificationTarget
} from './types';

interface NotificationCenterContextProps {
  notifications: NotificationCenterItem[];
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  registerDerivedNotification: (
    id: string,
    notification: NotificationCenterItem | null
  ) => void;
  registerAnchor: (
    anchorId: NotificationAnchorId,
    element: HTMLElement | null
  ) => void;
  /**
   * Lets the Correlation view register a callback that switches the active
   * correlation group tab when a scroll to one of its anchors is requested.
   * Needed because inactive group tabs are unmounted, so their anchor is not
   * in the DOM until the tab is selected.
   */
  registerCorrelationGroupResolver: (
    resolver: ((anchorId: NotificationAnchorId) => void) | null
  ) => void;
  navigateToTarget: (target: NotificationTarget) => Promise<void>;
}

const NotificationCenterContext = createContext<
  NotificationCenterContextProps | undefined
>(undefined);

const getRefinementTargetAnchor = (
  family: RefinementBlockFamily
): NotificationAnchorId =>
  family === 'water-oil'
    ? 'special-core-water-oil-refinement'
    : 'special-core-gas-oil-refinement';

const getRefinementSectionAnchor = (
  family: RefinementBlockFamily
): NotificationAnchorId =>
  family === 'water-oil' ? 'correlation-water-oil' : 'correlation-gas-oil';

export const NotificationCenterProvider = ({
  children
}: React.PropsWithChildren) => {
  const { data, updateConfigStates, updateData } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();
  const {
    preferenceEndpointSource,
    appliedEndpointSource,
    refinementPreferenceBlock
  } = useCorrelation();

  const [isOpen, setIsOpen] = useState(false);
  const [derivedNotifications, setDerivedNotifications] = useState<
    Record<string, NotificationCenterItem>
  >({});
  const anchorRegistryRef = useRef<Map<NotificationAnchorId, HTMLElement>>(
    new Map()
  );
  const pendingAnchorRef = useRef<NotificationAnchorId | null>(null);
  const correlationGroupResolverRef = useRef<
    ((anchorId: NotificationAnchorId) => void) | null
  >(null);

  const registerCorrelationGroupResolver = useCallback(
    (resolver: ((anchorId: NotificationAnchorId) => void) | null) => {
      correlationGroupResolverRef.current = resolver;
    },
    []
  );

  const attemptAnchorScroll = useCallback(
    (anchorId: NotificationAnchorId, attempt = 0) => {
      const anchor = anchorRegistryRef.current.get(anchorId);

      if (anchor) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (typeof anchor.focus === 'function') {
          anchor.focus({ preventScroll: true });
        }
        pendingAnchorRef.current = null;
        return;
      }

      if (attempt < 20) {
        window.setTimeout(() => attemptAnchorScroll(anchorId, attempt + 1), 75);
      }
    },
    []
  );

  const registerAnchor = useCallback(
    (anchorId: NotificationAnchorId, element: HTMLElement | null) => {
      if (element) {
        anchorRegistryRef.current.set(anchorId, element);
      } else {
        anchorRegistryRef.current.delete(anchorId);
      }

      if (element && pendingAnchorRef.current === anchorId) {
        attemptAnchorScroll(anchorId);
      }
    },
    [attemptAnchorScroll]
  );

  const queueAnchorScroll = useCallback(
    (anchorId: NotificationAnchorId) => {
      // Ask the Correlation view to switch to the group that owns this anchor
      // (a no-op for other anchors) so the target panel mounts before we scroll.
      correlationGroupResolverRef.current?.(anchorId);
      pendingAnchorRef.current = anchorId;
      window.setTimeout(() => attemptAnchorScroll(anchorId), 0);
    },
    [attemptAnchorScroll]
  );

  const activateCorrelationView = useCallback(
    (anchorId: NotificationAnchorId) => {
      updateConfigStates({
        dashOption: DashboardOptions.CORRELATION,
        showAnalyze: true,
        showDash: false,
        showResults: false,
        showSpecialCore: false,
        showGheInputs: false,
        showGheResults: false
      });
      queueAnchorScroll(anchorId);
    },
    [queueAnchorScroll, updateConfigStates]
  );

  const activateSpecialCoreView = useCallback(
    async (
      family: RefinementBlockFamily,
      anchorId: NotificationAnchorId,
      sampleUid?: string,
      wellUid?: string,
      projectUid?: string
    ) => {
      if (!selectedInfo.project?.uid || selectedInfo.project.uid !== projectUid) {
        return;
      }

      let wells = data.wells;
      let targetWell = wells.find(well => well.uid === wellUid);

      if (!targetWell && projectUid) {
        wells = await listWells(projectUid);
        wells.sort((a, b) => a.well_name.localeCompare(b.well_name));
        updateData({ wells });
        targetWell = wells.find(well => well.uid === wellUid);
      }

      if (!targetWell) {
        return;
      }

      let wellSamples = data.wellSamples;
      if (selectedInfo.well?.uid !== targetWell.uid || wellSamples.length === 0) {
        updateConfigStates({ loadingSamples: true });
        wellSamples = await listWellSamples(targetWell.uid);
        updateData({ wellSamples });
        updateConfigStates({ loadingSamples: false, fetchSamples: false });
      }

      const targetSample = sampleUid
        ? wellSamples.find(sample => sample.uid === sampleUid)
        : selectedInfo.sample;

      if (!targetSample) {
        return;
      }

      setSelectedInfo({
        well: targetWell,
        sample: targetSample
      });
      updateConfigStates({
        dashOption: DashboardOptions.SPECIAL_CORE,
        showDash: false,
        showAnalyze: false,
        showResults: false,
        showSpecialCore: true,
        showGheInputs: false,
        showGheResults: false,
        showRefinementTable: true,
        krType:
          family === 'water-oil' ? KrTypes.WATER_OIL : KrTypes.GAS_OIL
      });
      queueAnchorScroll(anchorId);
    },
    [
      data.wellSamples,
      data.wells,
      queueAnchorScroll,
      selectedInfo.project?.uid,
      selectedInfo.sample,
      selectedInfo.well?.uid,
      setSelectedInfo,
      updateConfigStates,
      updateData
    ]
  );

  const navigateToTarget = useCallback(
    async (target: NotificationTarget) => {
      setIsOpen(false);

      if (target.type === 'section') {
        if (target.view === 'correlation') {
          activateCorrelationView(target.anchorId);
          return;
        }

        await activateSpecialCoreView(
          target.family ?? 'water-oil',
          target.anchorId
        );
        return;
      }

      await activateSpecialCoreView(
        target.family,
        target.anchorId,
        target.sampleUid,
        target.wellUid,
        target.projectUid
      );
    },
    [activateCorrelationView, activateSpecialCoreView]
  );

  const registerDerivedNotification = useCallback(
    (id: string, notification: NotificationCenterItem | null) => {
      setDerivedNotifications(current => {
        if (!notification) {
          if (!(id in current)) {
            return current;
          }

          const next = { ...current };
          delete next[id];
          return next;
        }

        return {
          ...current,
          [id]: notification
        };
      });
    },
    []
  );

  const refinementNotification = useMemo<NotificationCenterItem | null>(() => {
    const projectUid = selectedInfo.project?.uid;

    if (!refinementPreferenceBlock || !projectUid) {
      return null;
    }

    const targetFamily =
      refinementPreferenceBlock.families.length === 1
        ? refinementPreferenceBlock.families[0]
        : undefined;

    return {
      id: 'correlation-refinement-values',
      kind: 'refinement-values',
      severity: 'warning',
      title: 'Refinement values unavailable',
      message: refinementPreferenceBlock.message,
      count: refinementPreferenceBlock.count,
      sampleCodes: refinementPreferenceBlock.sampleCodes,
      createdAt: 'current',
      current: true,
      target: {
        type: 'section',
        view: 'correlation',
        anchorId:
          targetFamily != null
            ? getRefinementSectionAnchor(targetFamily)
            : 'correlation-root'
      },
      children: refinementPreferenceBlock.samples.map(sample => ({
        id: `refinement-sample-${sample.sampleUid}`,
        label: sample.sampleCode,
        target: {
          type: 'sample',
          view: 'special-core',
          anchorId: getRefinementTargetAnchor(sample.family),
          family: sample.family,
          sampleUid: sample.sampleUid,
          wellUid: sample.wellUid,
          projectUid
        }
      }))
    };
  }, [refinementPreferenceBlock, selectedInfo.project?.uid]);

  const endpointPreferenceNotification = useMemo<NotificationCenterItem | null>(() => {
    if (preferenceEndpointSource === appliedEndpointSource) {
      return null;
    }

    return {
      id: 'correlation-endpoint-preference',
      kind: 'endpoint-source-unsaved',
      severity: 'info',
      title: 'Correlation endpoint source has unsaved changes',
      message: 'Correlation endpoint source has unsaved changes.',
      count: 1,
      sampleCodes: [],
      createdAt: 'current',
      current: true,
      target: {
        type: 'section',
        view: 'correlation',
        anchorId: 'correlation-root'
      }
    };
  }, [appliedEndpointSource, preferenceEndpointSource]);

  const notifications = useMemo(() => {
    const allNotifications = [
      refinementNotification,
      endpointPreferenceNotification,
      ...Object.values(derivedNotifications)
    ].filter(Boolean) as NotificationCenterItem[];

    return allNotifications.sort((left, right) => left.id.localeCompare(right.id));
  }, [
    derivedNotifications,
    endpointPreferenceNotification,
    refinementNotification
  ]);

  return (
    <NotificationCenterContext.Provider
      value={{
        notifications,
        isOpen,
        setIsOpen,
        registerDerivedNotification,
        registerAnchor,
        registerCorrelationGroupResolver,
        navigateToTarget
      }}
    >
      {children}
    </NotificationCenterContext.Provider>
  );
};

export const useNotificationCenter = () => {
  const context = useContext(NotificationCenterContext);

  if (!context) {
    throw new Error(
      'useNotificationCenter must be used within a NotificationCenterProvider'
    );
  }

  return context;
};

export const useNotificationAnchor = (anchorId: NotificationAnchorId) => {
  const { registerAnchor } = useNotificationCenter();

  return useCallback(
    (element: HTMLElement | null) => {
      registerAnchor(anchorId, element);
    },
    [anchorId, registerAnchor]
  );
};
